import { useId, useRef, useState } from "react";

import { Box, Collapse, Stack, useTheme } from "@mui/material";
import { FormType } from "@simplyk/common";
import { useWatch } from "react-hook-form";

import { useFormV2Context } from "../../../contexts/FormV2Context";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { isEmailAndAllowPlus, isRequired } from "@/common/helpers/validators";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormCheckbox } from "@/forms-design-system/FormCheckbox";
import { FormSelectField } from "@/forms-design-system/FormSelectField/FormSelectField";
import { FormSelectItem } from "@/forms-design-system/FormSelectField/FormSelectItem";
import { FormTextField } from "@/forms-design-system/FormTextField";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- Public-form components consume the editor preview context.
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- Match the editor's disabled preview styling.
import { GHOST_PREVIEW_OPACITY } from "@/features/LiveFormEditor/LivePreview/ghostPreview.constant";

export const InHonour = () => {
  const { disabledAdvancedSetting } = usePreviewContext();
  const isGhost = disabledAdvancedSetting === "allowInHonour";
  const { t } = useTranslate();
  const theme = useTheme();
  const { logAmplitudeEventCallback } = useAmplitude();
  const { formObject, form, formCountry, isEmbed, isPreview } = useFormV2Context(FormType.DonationForm);
  const { errors } = form.formState;

  const notifyEmailCheckboxId = useId();
  const inHonourNameId = useId();
  const inHonourNotifyEmailId = useId();
  const inHonourMessageId = useId();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const isInHonour = useWatch({ control: form.control, name: "inHonourOf" });
  const notifySomeone = useWatch({ control: form.control, name: "notifySomeone" });

  const handleInHonourChange = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked && !isPreview) {
      // Small delay to ensure the dropdown is rendered before opening
      setTimeout(() => setIsDropdownOpen(true), 100);
    }
  };

  const handleTributeChange = () => {
    setTimeout(() => {
      nameInputRef.current?.focus();
    }, 100);
  };

  const amplitudePayload = {
    isEmbed,
    formCategory: formObject.category,
    formId: formObject.id,
    organizationCountry: formCountry,
  };

  return (
    <Stack gap={2} id="in-honour-section" sx={{ opacity: isGhost ? GHOST_PREVIEW_OPACITY : 1 }}>
      {/* Main checkbox to enable donation in honor of someone */}
      <FormCheckbox
        control={form.control}
        name="inHonourOf"
        id={useId()}
        label={t("donationForm", "donationInHonourOrMemoryOfSomeone")}
        data-test="donation-form-in-honour-of"
        onChange={handleInHonourChange}
      />

      <Collapse in={isInHonour} easing="ease-in-out" unmountOnExit>
        <Stack gap={2} sx={{ pl: { md: 3.5 } }}>
          <Stack direction="row" spacing={1}>
            <Box sx={{ position: "relative", flex: 1 }}>
              <FormSelectField
                control={form.control}
                name="tribute"
                required={!isPreview}
                rules={isPreview ? [] : [isRequired(isInHonour, t("donationForm", "tributeRequired"))]}
                error={isPreview ? false : !!errors.tribute?.message}
                helperText={isPreview ? undefined : errors.tribute?.message}
                placeholder={t("common", "selectAnOptionPlaceholder")}
                aria-label={t("donationForm", "a11y.tributeType")}
                open={isDropdownOpen}
                onOpen={() => setIsDropdownOpen(true)}
                onClose={() => setIsDropdownOpen(false)}
                onChange={handleTributeChange}
                renderValue={(value) => {
                  if (value === "inHonourOf") {
                    return t("donationForm", "donationInHonorOf");
                  }
                  if (value === "inMemoryOf") {
                    return t("donationForm", "donationInMemoryOf");
                  }
                  return value;
                }}
              >
                <FormSelectItem value="inHonourOf">{t("donationForm", "donationInHonorOf")}</FormSelectItem>
                <FormSelectItem value="inMemoryOf">{t("donationForm", "donationInMemoryOf")}</FormSelectItem>
              </FormSelectField>
              {/* Expanded dropdown menu visual for preview */}
              {isPreview && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 1,
                    mt: 0.5,
                    py: 1,
                    backgroundColor: theme.palette.formsComponents?.selectField?.popoverBackgroundColor,
                    borderRadius: 1,
                    boxShadow: 8,
                  }}
                >
                  <FormSelectItem value="inHonourOf">{t("donationForm", "donationInHonorOf")}</FormSelectItem>
                  <FormSelectItem value="inMemoryOf">{t("donationForm", "donationInMemoryOf")}</FormSelectItem>
                </Box>
              )}
            </Box>

            {/* Field for the honoree's name */}
            <Box sx={{ flex: 1 }}>
              <FormTextField
                control={form.control}
                name="inHonourName"
                placeholder={t("donationForm", "inHonourNamePlaceholder")}
                inputProps={{ "aria-label": t("donationForm", "a11y.honoreeName") }}
                rules={isPreview ? [] : [isRequired(isInHonour, t("donationForm", "inHonourNameRequired"))]}
                id={inHonourNameId}
                inputRef={nameInputRef}
                error={isPreview ? false : !!errors.inHonourName?.message}
                helperText={isPreview ? undefined : errors.inHonourName?.message}
                onFocus={logAmplitudeEventCallback(AmplitudeEvents.DonationFocusGeneralInput, {
                  label: "inHonourName",
                  ...amplitudePayload,
                })}
                required={!isPreview}
              />
            </Box>
          </Stack>

          <Stack>
            <FormCheckbox
              control={form.control}
              name="notifySomeone"
              id={notifyEmailCheckboxId}
              label={t("donationForm", "notifySomeoneForTheDonation")}
            />

            <Collapse in={notifySomeone} easing="ease-in-out" unmountOnExit>
              <Stack gap={2} sx={{ pl: { md: 3.5 }, pt: 2 }}>
                {/* Email field for notification recipient */}
                <FormTextField
                  control={form.control}
                  name="inHonourNotifyEmail"
                  rules={[
                    isRequired(notifySomeone, t("donationForm", "inHonourNotifyEmailRequired")),
                    isEmailAndAllowPlus(t("common", "emailFormat"), t("common", "emailFormat.wrongChar")),
                  ]}
                  id={inHonourNotifyEmailId}
                  label={t("donationForm", "inHonourNotifyEmailLabel")}
                  error={!!errors.inHonourNotifyEmail?.message}
                  helperText={errors.inHonourNotifyEmail?.message}
                  onFocus={logAmplitudeEventCallback(AmplitudeEvents.DonationFocusGeneralInput, {
                    label: "inHonourNotifyEmail",
                    ...amplitudePayload,
                  })}
                  autoFocus
                  required
                />

                {/* Message field for custom notification message */}
                <FormTextField
                  control={form.control}
                  name="inHonourMessage"
                  rules={[isRequired(notifySomeone, t("donationForm", "inHonourMessageRequired"))]}
                  id={inHonourMessageId}
                  multiline
                  rows={4}
                  label={t("donationForm", "inHonourMessageLabel")}
                  error={!!errors.inHonourMessage?.message}
                  helperText={errors.inHonourMessage?.message}
                  onFocus={logAmplitudeEventCallback(AmplitudeEvents.DonationFocusGeneralInput, {
                    label: "inHonourMessage",
                    ...amplitudePayload,
                  })}
                  required
                />
              </Stack>
            </Collapse>
          </Stack>
        </Stack>
      </Collapse>
    </Stack>
  );
};
